import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { License } from '../../../../types/licenses.types';
import useMapProductNames from './use-map-product-names';

interface Props {
  open: boolean;
  loading: boolean;
  licensesLoading: boolean;
  licenses: License[];
  selectedLicenses: string[];

  handleSelectedLicensesChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAddLicenses(): void;

  handleClose(): void;
}

const AddLicenseToUserDialog: React.FC<Props> = ({
  open,
  loading,
  licensesLoading,
  licenses,
  selectedLicenses,
  handleSelectedLicensesChange,
  handleAddLicenses,
  handleClose,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const mapProductName = useMapProductNames();

  const options = licenses.map((license) => ({ label: mapProductName(license.product), value: license._id }));

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'} transitionDuration={0}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('addLicenseToUserDialogTitle')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {licensesLoading ? (
          <PaiSpinningLoader />
        ) : (
          <Stack direction={'column'} gap={2}>
            <Typography className={classes.text}>{t('addLicenseToUserDialogContent')}</Typography>
            <Stack direction={'column'} gap={1}>
              {options.map((option) => (
                <PaiCheckbox
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  checked={selectedLicenses.includes(option.value)}
                  onChange={handleSelectedLicensesChange}
                />
              ))}
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('close')} size={'md'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('confirm')}
          size={'small'}
          border={'rounded'}
          action={handleAddLicenses}
          disabled={selectedLicenses.length === 0}
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default AddLicenseToUserDialog;
